import { useGeneralGetHook } from "api/hooks/allHooks";
import { buildOptimisticGroupMutationOptions } from "features/shared/lib/technical-config-tab/api/mutations";
import { useConfigDetailQuery, useConfigGroupsQuery } from "features/shared/lib/technical-config-tab/api/queryHooks";
import { useActiveScenarioName } from "features/technical-data/scenario/hooks/useActiveScenarioName";
import { useMutation, useQueryClient } from "react-query";

import type {
  SaveWindowConfigProps,
  ShadingSystemCatalogItem,
  WindowCatalogItem,
  WindowTypeGroup,
  WindowTypeGroupDetail,
} from "../model/types";

import { WINDOW_CONFIG_CACHE_KEYS } from "./cacheKeys";
import { deleteWindowConfig, saveWindowConfig } from "./callers";
import windowConfigTabUrls from "./urls";

export const useGetWindowTypeGroups = (projectId?: string) => {
  const scenario = useActiveScenarioName();
  return useConfigGroupsQuery<WindowTypeGroup>(
    WINDOW_CONFIG_CACHE_KEYS.groups(projectId, scenario),
    windowConfigTabUrls.groups(projectId, scenario),
    Boolean(projectId && scenario)
  );
};

export const useGetWindowTypeDetail = (projectId?: string, userDefinedType?: string) => {
  const scenario = useActiveScenarioName();
  const enabled = Boolean(projectId && userDefinedType && scenario);
  const url = enabled ? windowConfigTabUrls.groupDetail(projectId, userDefinedType as string, scenario) : undefined;
  return useConfigDetailQuery<WindowTypeGroupDetail>(
    WINDOW_CONFIG_CACHE_KEYS.detail(projectId, userDefinedType, scenario),
    url,
    enabled
  );
};

export const useGetWindowTypeCatalog = (enabled = true) =>
  useGeneralGetHook<WindowCatalogItem[]>(WINDOW_CONFIG_CACHE_KEYS.catalog, windowConfigTabUrls.windowCatalog(), {
    enabled,
  });

export const useGetShadingSystemCatalog = (windowTypeDbId?: number, enabled = true) =>
  useGeneralGetHook<ShadingSystemCatalogItem[]>(
    WINDOW_CONFIG_CACHE_KEYS.shadingCatalog(windowTypeDbId),
    windowConfigTabUrls.shadingCatalog(windowTypeDbId),
    {
      enabled: enabled && (windowTypeDbId === undefined || windowTypeDbId > 0),
    }
  );

export const useSaveWindowConfig = () => {
  const scenario = useActiveScenarioName();
  const queryClient = useQueryClient();
  return useMutation(
    (variables: SaveWindowConfigProps) => saveWindowConfig({ ...variables, scenario }),
    buildOptimisticGroupMutationOptions<WindowTypeGroup, SaveWindowConfigProps>({
      applyOptimisticPatch: (group) => ({ ...group, hasTechnology: true }),
      cacheKeys: WINDOW_CONFIG_CACHE_KEYS,
      getProjectId: (variables) => variables.projectId,
      getRecordId: (variables) => variables.userDefinedType,
      getScenario: () => scenario,
      matchesGroup: (group, variables) => group.userDefinedType === variables.userDefinedType,
      queryClient,
      successMessageKey: "TechnicalData.windows.saveSuccess",
    })
  );
};

export const useDeleteWindowConfig = () => {
  const scenario = useActiveScenarioName();
  const queryClient = useQueryClient();
  return useMutation(
    (variables: { projectId: string; userDefinedType: string }) => deleteWindowConfig({ ...variables, scenario }),
    buildOptimisticGroupMutationOptions<WindowTypeGroup, { projectId: string; userDefinedType: string }>({
      applyOptimisticPatch: (group) => ({ ...group, hasTechnology: false }),
      cacheKeys: WINDOW_CONFIG_CACHE_KEYS,
      getProjectId: (variables) => variables.projectId,
      getRecordId: (variables) => variables.userDefinedType,
      getScenario: () => scenario,
      matchesGroup: (group, variables) => group.userDefinedType === variables.userDefinedType,
      queryClient,
      successMessageKey: "TechnicalData.windows.deleteSuccess",
    })
  );
};
